/**
 * The Knowledge Archive's 64px header — title, node count, search.
 *
 * Search lives here rather than in the filter panel on purpose: on this page
 * "find a specific concept" is the primary action, not one of four ways to
 * narrow a set. It writes the same `?q=` param the panel reads, so the two stay
 * in sync without either owning the other.
 *
 * The count sits in a **fixed-width slot** (`w-28`) so its value changing —
 * `1 concept` to `1,247 of 2,000 concepts` — cannot shove the search input
 * sideways. `NodeCountBadge` renders a transparent placeholder rather than
 * nothing for the same reason; see §2.10 #8.
 *
 * `lg:h-16` matches the filter panel's expanded header, so the two bottom
 * borders line up across the layout's gap. That alignment is the only reason
 * the height is fixed rather than derived from content.
 *
 * **Below `lg` the search wraps onto its own full-width row.** Title, count and
 * a fixed-width search do not fit one row until there is ~600px of header:
 * at 390 the title truncated to "K…", and between `md` and `lg` (desktop
 * layout, 350px panel beside it) it lost half its letters. Wrapping keeps the
 * page title whole at the type scale instead of shrinking it, and the count
 * stays beside the title where it reads as "Knowledge Archive · 12 concepts".
 * The price is that the two borders only line up from `lg`.
 */

import { ConceptSearchInput } from '@/components/features/mindspace/concepts/ConceptSearchInput';
import { NodeCountBadge } from '@/components/features/mindspace/graph/NodeCountBadge';

interface KnowledgeArchiveHeaderProps {
  visibleCount: number;
  totalCount: number | null;
}

export function KnowledgeArchiveHeader({
  visibleCount,
  totalCount,
}: KnowledgeArchiveHeaderProps) {
  return (
    <header className='flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-3 lg:h-16 lg:flex-nowrap lg:py-0'>
      <h2 className='min-w-0 text-xl font-semibold text-foreground lg:truncate'>
        Knowledge Archive
      </h2>
      <div className='ml-auto w-28 flex-shrink-0 text-right'>
        <NodeCountBadge visible={visibleCount} total={totalCount} />
      </div>
      <div className='w-full lg:w-72'>
        <ConceptSearchInput />
      </div>
    </header>
  );
}
